import type { ReactNode } from 'react';
import type { FormikProps } from 'formik';
import {
  FormControlInput,
  FormControlPhone,
  FormControlTextArea,
} from '../../../components/FormControls';
import type { FormField } from '../schema/types';

export type FieldRenderContext = {
  form: FormikProps<any>;
  isSubmitted: boolean;
  t: (key: string) => string;
  dynamicOptions?: Record<string, Array<{ value: unknown; label: string }>>;
  disabled?: boolean;
};

type FieldRenderer = (
  field: FormField,
  context: FieldRenderContext,
) => ReactNode;

const controlStyle = { width: '100%' } as const;

const fieldRenderers: Record<string, FieldRenderer> = {
  text: (field, { form, t, disabled }) => {
    const labelKey = field.labelKey ?? field.name;
    const placeholderKey = field.placeholderKey ?? labelKey;
    return (
      <FormControlInput
        key={field.name}
        name={field.name}
        label={t(labelKey)}
        placeholder={t(placeholderKey)}
        form={form}
        isRequired={field.validation?.required ?? false}
        isValidate
        height={38}
        width="100%"
        style={controlStyle}
        className="form-engine__control"
        disabled={disabled || form.isSubmitting}
      />
    );
  },

  email: (field, { form, t, disabled }) => {
    const labelKey = field.labelKey ?? field.name;
    const placeholderKey = field.placeholderKey ?? labelKey;
    return (
      <FormControlInput
        key={field.name}
        name={field.name}
        type="email"
        label={t(labelKey)}
        placeholder={t(placeholderKey)}
        form={form}
        isRequired={field.validation?.required ?? false}
        isValidate
        height={38}
        width="100%"
        style={controlStyle}
        className="form-engine__control"
        disabled={disabled || form.isSubmitting}
      />
    );
  },

  phone: (field, { form, isSubmitted, disabled }) => {
    const labelKey = field.labelKey ?? field.name;
    return (
      <FormControlPhone
        key={field.name}
        name={field.name}
        label={labelKey}
        form={form}
        isRequired={field.validation?.required ?? false}
        isSubmitted={isSubmitted || Boolean(form.touched?.[field.name])}
        width="100%"
        className="form-engine__control"
        disabled={disabled || form.isSubmitting}
      />
    );
  },

  textarea: (field, { form, t, disabled }) => {
    const labelKey = field.labelKey ?? field.name;
    const placeholderKey = field.placeholderKey ?? labelKey;
    return (
      <FormControlTextArea
        key={field.name}
        name={field.name}
        label={t(labelKey)}
        placeholder={t(placeholderKey)}
        form={form}
        isRequired={field.validation?.required ?? false}
        isValidate
        rows={field.rows ?? 3}
        height={120}
        width="100%"
        style={controlStyle}
        className="form-engine__control"
        disabled={disabled || form.isSubmitting}
      />
    );
  },
};

export function getFieldRenderer(fieldType: string): FieldRenderer {
  const renderer = fieldRenderers[fieldType];
  if (!renderer) {
    throw new Error(`[fieldRegistry] Unknown field type: ${fieldType}`);
  }
  return renderer;
}

export function registerFieldRenderer(
  fieldType: string,
  renderer: FieldRenderer,
): void {
  fieldRenderers[fieldType] = renderer;
}

export function hasFieldRenderer(fieldType: string): boolean {
  return Boolean(fieldRenderers[fieldType]);
}
